import { css } from '@emotion/react';
import { Typography, LinearProgress, Paper, TextField } from '@mui/material';
import EnhancedTable, { PAGE_SIZE_OPTIONS } from '../Table/EnhancedTable';
import { useColumns } from './columns';
import CancelButton from './CancelButton';
import DeleteButton from './DeleteButton';
import StartButton from './StartButton';

import useAutofetchedRunsAsync from 'store/useAutofetchedRunsAsync';
import useDebouncedSearchInput from 'hooks/useDebouncedSearchInput';
import useListStateParams, { oneOfParam, positiveIntParam, stringParam } from 'hooks/useListStateParams';

import { toggle, unique } from 'utils/array';

import IRunEntry from 'types/IRunEntry';
import { Dispatch, SetStateAction, useCallback } from 'react';

const listParams = {
  page: positiveIntParam(1),
  pageSize: oneOfParam(PAGE_SIZE_OPTIONS, 25),
  search: stringParam(),
};

export default function Runs(props: {
  selectedRunIds: string[];
  setSelectedRunIds: Dispatch<SetStateAction<string[]>>;
}) {
  const { selectedRunIds, setSelectedRunIds } = props;

  const { state, update } = useListStateParams(listParams);
  const { page, pageSize } = state;

  const applySearch = useCallback((value: string) => update({ search: value, page: 1 }), [update]);
  const [search, handleSearchChange] = useDebouncedSearchInput(state.search, applySearch);

  const runsAsync = useAutofetchedRunsAsync({
    page,
    pageSize,
    search: state.search || undefined,
  });

  const { status } = runsAsync;
  const paginatedData = runsAsync.status === 'SUCCESS' || runsAsync.status === 'REFRESH' ? runsAsync.data : undefined;
  const runs = paginatedData?.items ?? [];
  const total = paginatedData?.total ?? 0;

  const columns = useColumns();

  const handlePageChange = useCallback((newPage: number) => update({ page: newPage }), [update]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => update({ pageSize: newPageSize, page: 1 }),
    [update],
  );

  return (
    <section>
      <Paper elevation={1} style={{ padding: 48 }}>
        <Typography component="h1" variant="h6">
          Workflows
        </Typography>
        <br />
        {status === 'ERROR' && <p>Something went wrong</p>}
        <TextField
          id="search-workflow-runs"
          label="Search"
          variant="outlined"
          fullWidth
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <br />
        <br />
        {['INIT', 'PENDING'].includes(status) && <LinearProgress />}
        <EnhancedTable<IRunEntry>
          columns={columns}
          items={runs}
          selection={{
            isSelected: (runId: string) => selectedRunIds.includes(runId),
            selectOnly: (runId: string) => setSelectedRunIds([runId]),
            multi: {
              someSelected: runs.some((run) => selectedRunIds.includes(run.id)),
              allSelected: runs.length > 0 && runs.every((run) => selectedRunIds.includes(run.id)),
              toggle: (runId: string) => setSelectedRunIds(toggle(runId)),
              selectAll: () => setSelectedRunIds((ids) => ids.concat(runs.map((run) => run.id)).filter(unique)),
              deselectAll: () => setSelectedRunIds((ids) => ids.filter((id) => !runs.some((run) => run.id === id))),
            },
          }}
          serverPagination={{
            page,
            pageSize,
            total,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
        />
        <br />
        <div
          css={css`
            display: flex;

            > * ~ * {
              margin-left: 16px;
            }
          `}
        >
          <DeleteButton selectedRuns={runs.filter((run) => selectedRunIds.includes(run.id))} />
          <CancelButton selectedRuns={runs.filter((run) => selectedRunIds.includes(run.id))} />
          <StartButton selectedRuns={runs.filter((run) => selectedRunIds.includes(run.id))} />
        </div>
      </Paper>
    </section>
  );
}
